import Fuse, { FuseSearchOptions } from "fuse.js";
import { Tab } from "../types/Tab";
import { Season } from "../types/Season";
import { SortBy } from "../types/SortBy";
import { CatalogItemOptionValue, CatalogObject } from "square";

// Structural shape of the itemVariationData field (CatalogItemVariationData is
// not exported by all Square SDK versions, so we use a structural alias).
type ItemVariationData = {
  itemOptionValues?: Array<{ itemOptionValueId?: string }>;
  imageIds?: string[];
};

export const capitalizeFirstLetter = (string?: string) => {
  if (string == null) return null;
  return string
    .toLowerCase()
    .split(" ")
    .map((word) =>
      word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : "",
    )
    .join(" ");
};

/**
 * The merchant-chosen display position of an item WITHIN one category — the
 * order set by Square's dashboard "Rearrange Items" screen (Items → Categories
 * → ⋯ → Rearrange). Square stores it as an `ordinal` on the item's entry for
 * that category, ascending, and the values are large negatives (it inserts by
 * halving the gap between neighbours rather than renumbering the list).
 *
 * The SDK types `ordinal` as bigint, but it crosses the wire as a JSON number
 * (get-catalog's BigInt replacer), so accept either. An item with no ordinal
 * for this category sorts last rather than jumping to the front — a missing
 * value means "unplaced", not "first".
 */
const categoryOrdinal = (
  item: CatalogObject,
  categoryId?: string,
): number | undefined => {
  const entry = categoryId
    ? item.itemData?.categories?.find((c) => c.id === categoryId)
    : undefined;
  const raw = entry?.ordinal ?? item.itemData?.reportingCategory?.ordinal;
  if (raw === null || raw === undefined) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * The legacy numeric **"Order"** custom attribute set per item in Square
 * (10, 20, 30 …). It drove product order until 2026-08-23 and is still
 * maintained in Square as a deliberate fallback — see
 * `docs/catalog-change-checklist.md` § "Product display order".
 *
 * Square gives the value a definition-specific key, so match on the attribute's
 * TYPE rather than its name: the first NUMBER-typed attribute on the item wins.
 * It arrives as a string (`numberValue: "40.0"`).
 */
const orderAttribute = (item: CatalogObject): number | undefined => {
  for (const key in item.customAttributeValues) {
    const attr = item.customAttributeValues[key];
    if (attr?.type !== "NUMBER") continue;
    const n = parseFloat(attr.numberValue ?? "");
    if (Number.isFinite(n)) return n;
  }
  return undefined;
};

/**
 * Compare two items for merchandising order within ONE category.
 *
 * **Ordinal first, then the "Order" custom attribute.** The two are different
 * scales (ordinals are large negatives; the attribute is 10/20/30) so they can
 * never be compared against each other — the fallback applies only when the
 * ordinal can't decide, i.e. neither item has one for this category. An item
 * with no position under EITHER scheme sinks to the end: unknown means
 * "unplaced", not "first".
 */
const compareMerchandising = (
  a: CatalogObject,
  b: CatalogObject,
  categoryId?: string,
): number => {
  const oa = categoryOrdinal(a, categoryId);
  const ob = categoryOrdinal(b, categoryId);
  if (oa !== undefined && ob !== undefined) {
    return oa < ob ? -1 : oa > ob ? 1 : 0;
  }

  // Neither ordinal could decide — fall back to the legacy "Order" attribute.
  const aa = orderAttribute(a);
  const ab = orderAttribute(b);
  if (aa !== undefined && ab !== undefined) {
    return aa < ab ? -1 : aa > ab ? 1 : 0;
  }
  if (aa !== undefined) return -1;
  if (ab !== undefined) return 1;

  // No attribute either: whichever still has an ordinal is at least placed.
  if (oa !== undefined) return -1;
  if (ob !== undefined) return 1;
  return 0;
};

/**
 * A CATEGORY's own display position, as set by dragging categories in the Square
 * dashboard. Square records a child category's position under its parent on
 * `categoryData.parentCategory.ordinal` — NOT on `categoryData.ordinal`, which
 * is always null here and is the easy thing to reach for by mistake.
 *
 * Same scale and direction as item ordinals: ascending, large negatives.
 */
const categoryPosition = (category: CatalogObject): number | undefined => {
  const raw = category.categoryData?.parentCategory?.ordinal;
  if (raw === null || raw === undefined) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Sort CATEGORIES into the owner's dashboard order (e.g. Hats before T-Shirts).
 * Used for the nav dropdown and anywhere else categories are listed, so the
 * dashboard is the single place that decides. Categories with no position fall
 * back to alphabetical, so a newly created one lands somewhere sensible instead
 * of at a random spot.
 *
 * Returns a NEW array — the input belongs to CatalogContext.
 */
export const byCategoryOrder = (categories: CatalogObject[]): CatalogObject[] =>
  [...categories].sort((a, b) => {
    const pa = categoryPosition(a);
    const pb = categoryPosition(b);
    if (pa !== undefined && pb !== undefined) {
      return pa < pb ? -1 : pa > pb ? 1 : 0;
    }
    if (pa !== undefined) return -1;
    if (pb !== undefined) return 1;
    return (a.categoryData?.name ?? "").localeCompare(
      b.categoryData?.name ?? "",
      undefined,
      { sensitivity: "base" },
    );
  });

/**
 * Order a MIXED-category list of products — the homepage Classics/Designs
 * carousels, where items from several categories sit side by side.
 *
 * Two levels, both from the Square dashboard:
 *   1. the item's category position (so every hat precedes every t-shirt), then
 *   2. the item's own position within that category.
 *
 * A single ordinal can't express this on its own: item ordinals are only
 * comparable *within* one category, so sorting a mixed list by them interleaves
 * categories arbitrarily.
 *
 * `categories` is the full category list (from CatalogContext). An item is
 * placed by its first CHILD category — top-level ones are skipped, since every
 * item also carries the top-level "Apparel" and that would rank them all equal.
 */
export const byCategoryThenMerchandisingOrder = (
  items: CatalogObject[],
  categories?: CatalogObject[],
): CatalogObject[] => {
  const rankById = new Map<string, number>();
  // Tolerate a missing/late category list: items load independently of
  // categories, and a homepage that throws is worse than one briefly unsorted.
  byCategoryOrder(
    (categories ?? []).filter((c) => !c.categoryData?.isTopLevel),
  ).forEach((c, i) => {
    if (c.id) rankById.set(c.id, i);
  });

  const categoryOf = (item: CatalogObject): string | undefined =>
    item.itemData?.categories
      ?.map((c) => c.id)
      .find((id): id is string => !!id && rankById.has(id));

  return [...items].sort((a, b) => {
    const ca = categoryOf(a);
    const cb = categoryOf(b);
    // Uncategorised items sink rather than leading the carousel.
    const ra = ca === undefined ? Infinity : (rankById.get(ca) ?? Infinity);
    const rb = cb === undefined ? Infinity : (rankById.get(cb) ?? Infinity);
    if (ra !== rb) return ra < rb ? -1 : 1;
    return compareMerchandising(a, b, ca);
  });
};

/**
 * Merchandising order for a list of products WITHIN one category — the
 * arrangement the owner set by dragging in Square's "Rearrange Items" screen.
 * Pass the category being displayed; without one this falls back to the item's
 * reporting-category ordinal, which is the closest thing to a global sequence
 * Square offers. For a list that MIXES categories, use
 * `byCategoryThenMerchandisingOrder` instead.
 *
 * Returns a NEW array — callers pass arrays owned by CatalogContext, and
 * sorting one in place reorders shared state for every other consumer.
 *
 * Use this wherever a list should read as "the shop's own order". Do NOT apply
 * it to a list that already has a meaningful order of its own — search results
 * (relevance), an order's line items, or a wishlist — the order those arrive in
 * IS the answer.
 */
export const byMerchandisingOrder = (
  items: CatalogObject[],
  categoryId?: string,
): CatalogObject[] =>
  [...items].sort((a, b) => compareMerchandising(a, b, categoryId));

/** Selected browse-page facet values, keyed by facet. Empty arrays mean "any". */
export type ProductFilterSelection = {
  colorIds: string[];
  sizeIds: string[];
  priceRangeIds: string[];
};

export const EMPTY_PRODUCT_FILTERS: ProductFilterSelection = {
  colorIds: [],
  sizeIds: [],
  priceRangeIds: [],
};

/**
 * Price buckets for the browse-page filter, in integer cents like everything
 * else. Bounds are inclusive on both ends, and the buckets are contiguous so
 * every item lands in exactly one — selecting all three is the same as
 * selecting none.
 */
export const PRICE_RANGES: {
  id: string;
  name: string;
  minCents: number;
  maxCents: number;
}[] = [
  { id: "under-30", name: "Under $30", minCents: 0, maxCents: 2999 },
  { id: "30-50", name: "$30 to $50", minCents: 3000, maxCents: 5000 },
  { id: "over-50", name: "Over $50", minCents: 5001, maxCents: Infinity },
];

/** Cheapest variation price on an item, or undefined if it carries no price. */
export const lowestPriceCents = (item: CatalogObject): number | undefined => {
  const prices = (item.itemData?.variations ?? [])
    .map((v) => Number(v.itemVariationData?.priceMoney?.amount))
    .filter((n) => Number.isFinite(n));
  return prices.length ? Math.min(...prices) : undefined;
};

const variationOptionValueIds = (variation: CatalogObject): string[] =>
  (variation.itemVariationData?.itemOptionValues ?? [])
    .map((o) => o.itemOptionValueId ?? "")
    .filter(Boolean);

/**
 * Narrow a product list to the selected facet values.
 *
 * Values within one facet are OR'd (navy OR olive); facets are AND'd. Colour
 * and size must be satisfied by the SAME variation — a shirt that comes in navy
 * and comes in XL is not a match for "navy + XL" unless navy actually exists in
 * XL. Price is an item-level test against the cheapest variation, so a filtered
 * item still shows the price the card displays.
 *
 * Filtering is separate from sortProducts on purpose: sortProducts mutates
 * nothing but is called with the already-narrowed list, and mixing the two
 * would make "Featured" order depend on which facets happened to be open.
 */
export const filterProducts = (
  items: CatalogObject[],
  { colorIds, sizeIds, priceRangeIds }: ProductFilterSelection,
): CatalogObject[] => {
  const wantsColor = colorIds.length > 0;
  const wantsSize = sizeIds.length > 0;
  const wantsPrice = priceRangeIds.length > 0;
  if (!wantsColor && !wantsSize && !wantsPrice) return items;

  const ranges = PRICE_RANGES.filter((r) => priceRangeIds.includes(r.id));

  return items.filter((item) => {
    if (wantsPrice) {
      const cents = lowestPriceCents(item);
      if (cents == null) return false;
      if (!ranges.some((r) => cents >= r.minCents && cents <= r.maxCents)) {
        return false;
      }
    }

    if (!wantsColor && !wantsSize) return true;

    return (item.itemData?.variations ?? []).some((variation) => {
      const ids = variationOptionValueIds(variation);
      const colorOk = !wantsColor || colorIds.some((id) => ids.includes(id));
      const sizeOk = !wantsSize || sizeIds.some((id) => ids.includes(id));
      return colorOk && sizeOk;
    });
  });
};

/** Count of facet values currently selected, for the Filter button's badge. */
export const countSelectedFilters = (
  selection: ProductFilterSelection,
): number =>
  selection.colorIds.length +
  selection.sizeIds.length +
  selection.priceRangeIds.length;

export const sortProducts = (
  items: CatalogObject[],
  size?: CatalogItemOptionValue,
  sortBy?: SortBy,
  /** Category being displayed — ordinals are per item-category pair. */
  categoryId?: string,
): CatalogObject[] => {
  // Sort
  switch (sortBy) {
    case SortBy.Alphabetically_AZ:
      return items.sort((a, b) =>
        (a.itemData?.name ?? "").localeCompare(
          b.itemData?.name ?? "",
          undefined,
          {
            sensitivity: "base",
          },
        ),
      );
    case SortBy.Alphabetically_ZA:
      return items.sort((a, b) =>
        (b.itemData?.name ?? "").localeCompare(
          a.itemData?.name ?? "",
          undefined,
          {
            sensitivity: "base",
          },
        ),
      );
    case SortBy.Price_Low_to_High:
      return items.sort((a, b) =>
        Number(
          (a.itemData?.variations?.[0]?.itemVariationData?.priceMoney?.amount ??
            BigInt(0)) -
            (b.itemData?.variations?.[0]?.itemVariationData?.priceMoney
              ?.amount ?? BigInt(0)),
        ),
      );
    case SortBy.Price_High_to_Low:
      return items.sort((a, b) =>
        Number(
          (b.itemData?.variations?.[0]?.itemVariationData?.priceMoney?.amount ??
            BigInt(0)) -
            (a.itemData?.variations?.[0]?.itemVariationData?.priceMoney
              ?.amount ?? BigInt(0)),
        ),
      );
    case SortBy.Featured:
      // "Featured" IS the merchant's own arrangement — whatever order the owner
      // dragged the items into on Square's "Rearrange Items" screen. This is the
      // default sort on every category page, so the dashboard is authoritative
      // for how the shop reads.
      return byMerchandisingOrder(items, categoryId);
    case SortBy.Relevance: // This is handled by Fuse above
    case SortBy.Best_Sellers:
    default:
      return items;
  }
};

export const filterGlobalCategorySearch = (query: string, tabs: string[]) => {
  const values: { name: string }[] = tabs.map((t) => ({
    name: t.toString(),
  }));

  // Apply fuzzy search on productLine-filtered products
  const fuse = new Fuse(values, {
    keys: [{ name: "name", weight: 1.0 }],
    threshold: 0.3, // Stricter matching
    distance: 50, // Require closer matches
    useExtendedSearch: true, // Allow exact queries
    ignoreLocation: true, // Match regardless of position
    shouldSort: true, // Sort by relevance
  });

  const options: FuseSearchOptions = { limit: 3 };
  const result = fuse.search(query, options);
  return result.map((r) => r.item.name);
};

/**
 * Fuzzy-match products by NAME against the catalog the browser has already
 * downloaded. Name only, deliberately: descriptions are long marketing copy in
 * which almost every query finds a weak hit, so keying them in made short
 * searches return most of the catalog. The storefront loads the whole catalog into
 * CatalogContext (localStorage-cached), so product search is a local operation
 * — no network round-trip per keystroke. This also means search inherits the
 * server-side visibility filtering `get-catalog` applies, which a direct Square
 * text search does not.
 */
export const filterProductSearch = (
  query: string,
  items: CatalogObject[],
  limit = 12,
): CatalogObject[] => {
  const trimmed = query?.trim() ?? "";
  if (!trimmed) return [];

  const needle = trimmed.toLowerCase();
  const pool = items ?? [];

  // Literal name matches first. Fuzzy matching alone is far too generous on
  // short queries — "bison" scored Union Pacific Tee and two crewnecks
  // alongside the actual Bison Cap — so anything containing the typed text
  // outranks every fuzzy hit.
  const exact = pool.filter((item) =>
    (item.itemData?.name ?? "").toLowerCase().includes(needle),
  );
  if (exact.length >= limit) return exact.slice(0, limit);

  const seen = new Set(exact.map((i) => i.id));
  const fuse = new Fuse(
    pool
      .filter((item) => !seen.has(item.id))
      .map((item) => ({ item, name: item.itemData?.name ?? "" })),
    {
      keys: [{ name: "name", weight: 1.0 }],
      threshold: 0.3, // Tolerate a typo, not a different product.
      ignoreLocation: true, // Match anywhere in the name, not just the start.
      shouldSort: true,
    },
  );

  const fuzzy = fuse
    .search(trimmed, { limit } as FuseSearchOptions)
    .map((r) => r.item.item);

  return [...exact, ...fuzzy].slice(0, limit);
};

export const parameterToTabType = (category: string) => {
  return Tab[capitalizeFirstLetter(category) as keyof typeof Tab];
};

export const isSeason = (domain: string) => {
  const seasons = Object.keys(Season).map((s) => s.toString().toLowerCase());
  for (let i = 0; i < seasons.length; i++) {
    const season = seasons[i];
    if (domain.toLowerCase().includes(season)) {
      return true;
    }
  }
  return false;
};

export const countOccurrenceInString = (
  str: string,
  separator: string,
): number => {
  return str?.split(separator)?.length - 1;
};

export const sizeToString = (size: string) => {
  const sizeString = size?.toString();
  const countXs = countOccurrenceInString(sizeString, "X");
  return countXs > 1
    ? countXs?.toString() + "X" + sizeString?.replaceAll("X", "")
    : sizeString;
};

export const typeToString = <T>(typeString: T) => {
  return typeString?.toString()?.replaceAll("_", " ");
};

export const addHours = (date: Date, hours: number): Date => {
  const result = new Date(date); // Create a copy of the input date
  result.setHours(result.getHours() + hours); // Add the hours
  return result; // Return the new date
};

export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date); // Create a copy of the input date
  result.setDate(result.getDate() + days); // Add the days
  return result; // Return the new date
};

export const addMonths = (date: Date, numMonths: number) => {
  const newDate = new Date(date);
  // If next season is specified, add 3 months
  newDate.setMonth(newDate.getMonth() + numMonths);

  // Handle year rollover if necessary
  if (newDate.getDate() !== date.getDate()) {
    newDate.setDate(0); // Set to the last day of the previous month
  }

  return newDate;
};

export const getSeason = (next?: boolean): Season => {
  // TODO: Enhance to be more granular

  const currentDate = new Date();
  const newDate = next ? addMonths(currentDate, 1) : currentDate;

  const month = newDate.getMonth(); // 0 = January, 11 = December

  switch (month) {
    case 11:
    case 0:
    case 1:
      return Season.Winter;
    case 2:
    case 3:
    case 4:
      return Season.Spring;
    case 5:
    case 6:
    case 7:
      return Season.Summer;
    case 8:
    case 9:
    case 10:
      return Season.Fall;
    default:
      throw new Error("Invalid month");
  }
};

export const getCurrentYear = (): number => {
  return new Date().getFullYear();
};

export const getEndOfWeek = (): Date => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 (Sun) - 6 (Sat)
  const daysUntilEndOfWeek = 6 - dayOfWeek;

  const endOfWeek = new Date(now);
  endOfWeek.setDate(now.getDate() + daysUntilEndOfWeek);
  endOfWeek.setHours(23, 59, 59, 999); // Set to 11:59:59.999 PM

  return endOfWeek;
};

type GetLinkProps = {
  tab?: Tab;
  domain?: string;
  category?: string;
  itemName?: string;
};
export const getTabLink = ({
  tab,
  domain,
  category,
  itemName,
}: GetLinkProps): string => {
  let link = "";
  if (!!tab) {
    link += `/${tab.toString().toLowerCase()}`;
    switch (tab) {
      case Tab.Orders:
        return (link += "/track");
      case Tab.Contact:
      case Tab.About:
      case Tab.Podcast:
      case Tab.Home:
      default:
        return link;
    }
  }

  link += "/shop";
  if (!!domain) {
    link += `/${domain.toLowerCase()}`;
  }
  if (!!category) {
    link += `/category/${category.toLowerCase()}`;
  }
  if (!!itemName) {
    link += `/product/${itemName.toLowerCase()}`;
  }
  return link;
};

export const areArraysEqual = (arr1: any[], arr2: any[]): boolean => {
  if (arr1 == null && arr2 == null) return true;
  if ((arr1 == null && arr2 != null) || (arr1 != null && arr2 == null))
    return false;
  if (arr1.length !== arr2.length) return false;

  return arr1.every((obj1) =>
    arr2.some((obj2) => JSON.stringify(obj1) === JSON.stringify(obj2)),
  );
};

export const toUniqueList = (arr: any[]) => {
  return arr.filter(
    (item, index) => item != null && arr.indexOf(item) === index,
  );
};

export function getRandomDecimal(
  min: number,
  max: number,
  places: number = 2,
): number {
  var value = Math.random() * (max - min) + min;
  return parseFloat(value.toFixed(places));
}

/**
 * Comparator used to sort numbers while pushing null/undefined/NaN values to the end.
 * Returns a negative, zero, or positive value for use with Array.sort().
 */
export const nullishNumberCompare = (
  a: number | null,
  b: number | null,
): number => {
  const isANullish = a === null || a === undefined || isNaN(a);
  const isBNullish = b === null || b === undefined || isNaN(b);
  if (isANullish && isBNullish) return 0;
  if (isANullish) return 1;
  if (isBNullish) return -1;
  return a - b;
};

/**
 * Sort key for an image's position in the item's Square gallery
 * (`itemData.imageIds`) — the order the merchant sees and drags in the Square
 * dashboard, and the single source of truth for photo order on the storefront.
 *
 * An image the gallery doesn't list sorts LAST: `indexOf` returns -1, which
 * would otherwise beat position 0 and float an unlisted image to the front.
 */
export const galleryImageOrder = (
  imageId?: string | null,
  galleryImageIds?: string[] | null,
): number => {
  const index = (galleryImageIds ?? []).indexOf(imageId ?? "");
  return index === -1 ? NaN : index;
};

/**
 * Returns the images for a given color option value ID, ordered by their
 * position in the ITEM GALLERY.
 *
 * @param productVariations - Flat list of CatalogItemVariationData for the product
 * @param itemOptionValueId - The color option value ID to filter by
 * @param getImagesByIds - Catalog accessor to resolve image objects by their IDs
 * @param galleryImageIds - The item's own `itemData.imageIds`, in gallery order
 */
export const getImagesByColor = (
  productVariations: ItemVariationData[],
  itemOptionValueId: string,
  getImagesByIds: (ids: string[]) => CatalogObject[],
  galleryImageIds?: string[] | null,
): CatalogObject[] => {
  const variationsByColor = productVariations?.filter((v) =>
    v.itemOptionValues
      ?.map((iov: { itemOptionValueId?: string }) => iov.itemOptionValueId)
      .includes(itemOptionValueId),
  );
  const imageIds = toUniqueList(
    variationsByColor?.flatMap((v) => v.imageIds ?? []) ?? [],
  );
  return getImagesByIds(imageIds)?.sort(
    (x, y) =>
      nullishNumberCompare(
        galleryImageOrder(x.id, galleryImageIds),
        galleryImageOrder(y.id, galleryImageIds),
      ) ||
      // Tie-break (both absent from the gallery) on the variation's own image
      // order, so the result is deterministic rather than cache-insertion order.
      imageIds.indexOf(x.id) - imageIds.indexOf(y.id),
  );
};

/**
 * Orders a product's colour option values by where that colour's photos sit in
 * the ITEM GALLERY, so the swatch order — and therefore the colour a product
 * card and the product page lead with — is the order the merchant arranged in
 * the Square dashboard. Drag a colour's photo to the front of the gallery and
 * that colour leads the site.
 *
 * A colour is ranked by its EARLIEST gallery position, not by its first
 * variation image: a colour leads if any of its photos leads the gallery,
 * whichever order the variation happens to list its own images in.
 *
 * Colours with no photo in the gallery keep their original relative order and
 * sort after every colour that has one.
 *
 * This deliberately reads only ITEM data (`itemData.imageIds` and each
 * variation's `imageIds`) and never the fetched IMAGE objects. Images are
 * loaded lazily, well after the items (findOrGetImages in src/api/catalog.ts),
 * so a sort key living on the image object is simply absent on a cold visitor's
 * first render — the list then fell back to Square's raw variation order and
 * the lead colour latched to the wrong one, changing again on the next reload.
 * Ranking from the item itself is stable from the very first paint.
 */
export const sortColorsByGalleryOrder = <T extends string | undefined>(
  colorIds: T[],
  productVariations: ItemVariationData[],
  galleryImageIds?: string[] | null,
): T[] => {
  const ranks = colorIds.map((colorId) => {
    const imageIds = (productVariations ?? [])
      .filter((v) =>
        v.itemOptionValues
          ?.map((iov: { itemOptionValueId?: string }) => iov.itemOptionValueId)
          .includes(colorId ?? ""),
      )
      .flatMap((v) => v.imageIds ?? []);
    let best = NaN;
    for (const imageId of imageIds) {
      const rank = galleryImageOrder(imageId, galleryImageIds);
      if (!isNaN(rank) && (isNaN(best) || rank < best)) best = rank;
    }
    return best;
  });
  return colorIds
    .map((id, index) => ({ id, index }))
    .sort(
      (a, b) =>
        nullishNumberCompare(ranks[a.index], ranks[b.index]) ||
        a.index - b.index,
    )
    .map((entry) => entry.id);
};

export function getRandomInteger(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

/** Count of actual digits in a phone string, ignoring formatting. */
export const countPhoneDigits = (raw?: string | null): number =>
  (raw ?? "").replace(/\D/g, "").length;

/**
 * True when `raw` is a valid US phone number. We ship only within the United
 * States, so a US number is exactly 10 digits (area code + 7), optionally with
 * a leading "1" country code ("+1 …" → 11 digits). This is stricter than the
 * 9–16 range Square's Customers API tolerates on purpose: it catches the common
 * dropped-digit typo (9 digits) that Square would otherwise accept and store as
 * an unreachable number, breaking Express delivery. Empty is NOT valid —
 * checkout requires a phone, because Printful only quotes Express when the
 * recipient has one. If we ever ship internationally, relax this back toward
 * Square's 9–16 range (and make it country-aware).
 */
export const isValidPhoneNumber = (raw?: string | null): boolean => {
  const digitStr = (raw ?? "").replace(/\D/g, "");
  return (
    digitStr.length === 10 || (digitStr.length === 11 && digitStr[0] === "1")
  );
};

/**
 * Reduce a typed phone number to digits (preserving a leading "+") before it
 * goes to Square / Printful:
 *   "(650) 253-0000"   → "6502530000"
 *   "+1 650-253-0000"  → "+16502530000"
 *   "call me 6502530000" → "6502530000"
 *
 * Square tolerates spaces, "()", "-" and "." but not stray letters, so
 * normalizing is what keeps a lightly-mistyped number from bouncing. Returns ""
 * for input with no digits at all.
 */
export const normalizePhoneNumber = (raw?: string | null): string => {
  const trimmed = (raw ?? "").trim();
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  return trimmed.startsWith("+") ? `+${digits}` : digits;
};

/**
 * Human label for a Printful shipping method, taken from Printful's own `name`
 * with its redundant "(Estimated delivery: …)" suffix stripped — the delivery
 * window is rendered separately via formatDeliveryEstimate:
 *   "Flat Rate (Estimated delivery: Jul 24–27) "  →  "Flat Rate"
 *   "Express (Estimated delivery: Jul 21) "       →  "Express"
 *
 * Deliberately NOT a hardcoded id→label map. Printful returns methods beyond
 * STANDARD/PRINTFUL_FAST — international carts also quote PRINTFUL_MEDIUM_DDP —
 * and mapping an unknown id to a wrong label misprices the choice for the buyer.
 * Falls back to the raw id if the name is unusable.
 */
export const shippingMethodLabel = (method: {
  id: string;
  name?: string;
}): string =>
  (method.name ?? "")
    .replace(/\s*\(Estimated delivery:[^)]*\)\s*$/, "")
    .trim() || method.id;

/**
 * Format an estimated delivery window (from Printful's min/max delivery dates)
 * into a friendly string, collapsing shared month/year:
 *   "Jul 7–9, 2026"  ·  "Jun 30 – Jul 2, 2026"  ·  "Jul 7, 2026"
 * Returns null when no usable dates are supplied.
 */
export const formatDeliveryEstimate = (
  minDate?: string,
  maxDate?: string,
): string | null => {
  // Parse YYYY-MM-DD as a LOCAL date — `new Date("2026-07-07")` parses as UTC
  // midnight, which can render as the previous day in western timezones.
  const parse = (s?: string): Date | null => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const min = parse(minDate);
  const max = parse(maxDate);
  if (!min && !max) return null;

  const month = (dt: Date) =>
    dt.toLocaleDateString("en-US", { month: "short" });

  // Single date (only one supplied, or both identical).
  if (!min || !max || min.getTime() === max.getTime()) {
    const d = (min ?? max)!;
    return `${month(d)} ${d.getDate()}, ${d.getFullYear()}`;
  }

  // Same month and year → "Jul 7–9, 2026".
  if (
    min.getMonth() === max.getMonth() &&
    min.getFullYear() === max.getFullYear()
  ) {
    return `${month(min)} ${min.getDate()}–${max.getDate()}, ${min.getFullYear()}`;
  }

  // Same year, different month → "Jun 30 – Jul 2, 2026".
  if (min.getFullYear() === max.getFullYear()) {
    return `${month(min)} ${min.getDate()} – ${month(max)} ${max.getDate()}, ${min.getFullYear()}`;
  }

  // Spans a year boundary → spell out both years.
  return `${month(min)} ${min.getDate()}, ${min.getFullYear()} – ${month(max)} ${max.getDate()}, ${max.getFullYear()}`;
};

/**
 * Display label for a card on file: "Visa •••• 4242".
 *
 * Square's `cardBrand` is an UPPER_SNAKE enum (VISA, MASTERCARD, AMERICAN_EXPRESS),
 * which reads as shouting next to the rest of the checkout — title-case it rather
 * than mapping known brands, so a brand Square adds later still renders sanely.
 */
export const savedCardLabel = (card: {
  cardBrand?: string | null;
  last4?: string | null;
}): string => {
  const brand = (card.cardBrand ?? "")
    .split("_")
    .filter(Boolean)
    .map((word) =>
      // AMEX's own abbreviation stays uppercase; everything else title-cases.
      word.length <= 2
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");
  return [brand || "Card", card.last4 ? `•••• ${card.last4}` : ""]
    .filter(Boolean)
    .join(" ");
};

/** "08/28" for a saved card's expiry, or null when Square didn't return one. */
export const savedCardExpiry = (card: {
  expMonth?: number | null;
  expYear?: number | null;
}): string | null => {
  if (!card.expMonth || !card.expYear) return null;
  return `${String(card.expMonth).padStart(2, "0")}/${String(card.expYear).slice(-2)}`;
};

/**
 * Has a saved card passed its expiry? Cards expire at the END of their stated
 * month, and Square keeps returning them from ListCards afterwards — so without
 * this the customer is offered a card the issuer will decline.
 */
export const isSavedCardExpired = (
  card: { expMonth?: number | null; expYear?: number | null },
  now: Date = new Date(),
): boolean => {
  if (!card.expMonth || !card.expYear) return false;
  // First day of the month AFTER the expiry month; anything from then on is dead.
  const deadAt = new Date(card.expYear, card.expMonth, 1);
  return now >= deadAt;
};
